import { useNavigate } from '@tanstack/react-router';
import { memo, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import blurredBackground from '@/assets/bg-blurred.webp';
import CradlLogoWithText from '@/assets/cradl-logo-with-text.svg?react';
import { ErrorCard, Flex, Separator, WizardStepIndicator } from '@/components/core';
import { EXPORTS, TRIGGERS } from '@/components/flow';
import { createWizard, DEFAULT_TEMPLATE, WizardState } from '@/components/ui';
import { CREATE_AGENT_RUN_FROM_MANUAL_FUNCTION_ID, EXPORT_TO_MANUAL_FUNCTION_ID } from '@/constants';
import {
  ChooseIntegrationResult,
  ChooseIntegrationsStep,
  ChooseTemplateResult,
  ChooseTemplateStep,
} from '@/features/agents';
import { useScaffoldAgent, useTour } from '@/hooks';
import { toOrganizationId, useGetOrganization } from '@/hooks/api';

import { ConfigureCustomModelResult, ConfigureCustomModelStep } from './ConfigureCustomModelStep';
import { SetupAgentStep } from './SetupAgentStep';
import { Upgrade } from './Upgrade';

export type NewAgent = ChooseTemplateResult & ChooseIntegrationResult & ConfigureCustomModelResult;

const { useWizard, Wizard } = createWizard<NewAgent>();

const StepWrapper = ({ children }: PropsWithChildren) => {
  return (
    <Flex col variant="plain" className="py-md gap-md w-130 rounded-md border-1 border-gray-200 bg-white shadow-xl">
      <Flex>
        <CradlLogoWithText className="mx-md h-6" />
      </Flex>

      <Separator orientation="horizontal" />
      {children}

      <WizardStepIndicator useWizard={useWizard} hideLast />
    </Flex>
  );
};

export type NewProps = object;

// eslint-disable-next-line no-empty-pattern
const INew = ({}: NewProps) => {
  const { data: organization } = useGetOrganization(toOrganizationId('me'));
  const { create, agentId, status } = useScaffoldAgent();

  const [customModelSelected, setCustomModelSelected] = useState(false);

  const navigate = useNavigate();
  const { setTooltip } = useTour();

  useEffect(() => {
    if (status.isDone && agentId) {
      navigate({ to: '/agents/$agentId', params: { agentId } }).catch(console.error);
    }
  }, [status.isDone, navigate, agentId, setTooltip]);

  const onCreate = useCallback(
    (result: Partial<NewAgent> | undefined) => {
      // Hack to avoid creating a trigger/export if manual trigger/export is selected
      if (result?.trigger?.functionId?.includes('create-agent-run-from-manual')) result.trigger = undefined;
      if (result?.export?.functionId?.includes('export-to-manual')) result.export = undefined;

      const agent = create(result);
      if (agent.template === DEFAULT_TEMPLATE) {
        setTooltip([agentId], 'showSampleDocumentTooltip', false);
      }
      return agent;
    },
    [agentId, create, setTooltip]
  );

  const onSelectTemplate = useCallback((wizard: WizardState<NewAgent>, result: ChooseTemplateResult) => {
    setCustomModelSelected(result.template === DEFAULT_TEMPLATE);
    wizard.next(result);
  }, []);

  const allAgentsUsed = organization && organization.numberOfAgentsAllowed === organization.numberOfAgentsCreated;

  const defaultTrigger = TRIGGERS.find((t) => t.functionId === CREATE_AGENT_RUN_FROM_MANUAL_FUNCTION_ID);
  const defaultExport = EXPORTS.find((e) => e.functionId === EXPORT_TO_MANUAL_FUNCTION_ID);

  return (
    <div
      className="gap-sm relative flex h-screen w-full flex-col items-center justify-center overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${blurredBackground})` }}
    >
      {allAgentsUsed ? (
        <Upgrade />
      ) : (
        <Wizard className="gap-md">
          <Wizard.Step>
            {(wizard) => (
              <StepWrapper>
                <ChooseTemplateStep
                  defaultValue={wizard.state.template}
                  onContinue={(result) => onSelectTemplate(wizard, result)}
                />
              </StepWrapper>
            )}
          </Wizard.Step>

          {customModelSelected && (
            <Wizard.Step>
              {(wizard) => (
                <StepWrapper>
                  <ConfigureCustomModelStep onContinue={wizard.next} />
                </StepWrapper>
              )}
            </Wizard.Step>
          )}

          <Wizard.Step onNext={onCreate}>
            {(wizard) => (
              <StepWrapper>
                <ChooseIntegrationsStep
                  defaultOptions={{
                    trigger: wizard.state.trigger || defaultTrigger,
                    export: wizard.state.export || defaultExport,
                  }}
                  isCreatingAgent={status.agent.isPending}
                  onContinue={wizard.next}
                />
              </StepWrapper>
            )}
          </Wizard.Step>

          <Wizard.Step>
            {(wizard) => (
              <SetupAgentStep status={status} hasTrigger={!!wizard.state.trigger} hasExport={!!wizard.state.export} />
            )}
          </Wizard.Step>
        </Wizard>
      )}
      {status.errors.length > 0 ? (
        <div className="p-lg w-130">
          <ErrorCard label="Error encountered while setting up your agent" error={status.errors} />
        </div>
      ) : null}
    </div>
  );
};

export const New = memo(INew) as typeof INew;
